import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useChurchPlanner, usePlannerRoles } from '../../hooks/useChurchPlanner';
import { PLANNER_TEAM_DEFINITIONS, resolveTeamIdFromRole } from '../../hooks/usePlannerInsights';
import {
  formatPlannerStatusLabel,
  mergePlannerStatuses,
  normalizePlannerStatus,
  resolvePlannerServiceStatus,
  type PlannerResolvedStatus,
} from './plannerStatus';
import { PlannerCard, PlannerSubtle, plannerColors } from './plannerTheme';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaUsers as FaUsersRaw,
} from 'react-icons/fa';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUsers: any = FaUsersRaw;

const SnapshotCard = styled(PlannerCard)<{ $compact?: boolean }>`
  min-height: ${({ $compact }) => ($compact ? '0' : '420px')};
`;

const SnapshotHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const SnapshotTitle = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${plannerColors.muted};
`;

const ActivityList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ActivityItem = styled.li`
  font-size: 0.8rem;
  color: ${plannerColors.muted};
  line-height: 1.4;
  padding-left: 0.75rem;
  border-left: 2px solid ${plannerColors.border};
`;

const TeamList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const TeamMemberItem = styled.li`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background: ${plannerColors.surfaceHover};
  padding: 0.4rem 0.45rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const TeamMemberName = styled.div`
  color: ${plannerColors.text};
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TeamMemberLink = styled(Link)`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: inherit;
  text-decoration: none;

  &:hover ${TeamMemberName} {
    color: ${plannerColors.accent};
  }
`;

const TeamMemberText = styled.div`
  min-width: 0;
`;

const TeamMemberAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  object-fit: cover;
  flex: 0 0 auto;
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surface};
`;

const TeamMemberAvatarFallback = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: ${plannerColors.accentSoft};
  color: ${plannerColors.accent};
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.03em;
`;

const TeamMemberMeta = styled.div`
  margin-top: 0.1rem;
  color: ${plannerColors.muted};
  font-size: 0.64rem;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TeamGroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const TeamGroupCard = styled.section`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  padding: 0.55rem;
`;

const TeamGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.45rem;
  margin-bottom: 0.35rem;
`;

const TeamGroupTitle = styled.h4`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TeamGroupCount = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MemberStatusPill = styled.span<{ $tone: SnapshotAvailability }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.24rem;
  border-radius: 999px;
  padding: 0.14rem 0.42rem;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'confirmed'
        ? 'rgba(229, 211, 179, 0.42)'
        : $tone === 'available'
        ? 'rgba(52, 199, 89, 0.38)'
        : $tone === 'maybe'
          ? 'rgba(255, 204, 0, 0.38)'
          : $tone === 'unavailable'
            ? 'rgba(255, 69, 58, 0.38)'
            : 'rgba(165, 176, 194, 0.32)'};
  background: ${({ $tone }) =>
    $tone === 'confirmed'
      ? 'rgba(229, 211, 179, 0.14)'
      : $tone === 'available'
      ? 'rgba(52, 199, 89, 0.12)'
      : $tone === 'maybe'
        ? 'rgba(255, 204, 0, 0.12)'
        : $tone === 'unavailable'
          ? 'rgba(255, 69, 58, 0.12)'
          : 'rgba(165, 176, 194, 0.12)'};
  color: ${({ $tone }) =>
    $tone === 'confirmed'
      ? plannerColors.accent
      : $tone === 'available'
      ? plannerColors.success
      : $tone === 'maybe'
        ? plannerColors.warning
        : $tone === 'unavailable'
          ? plannerColors.danger
          : plannerColors.muted};
`;

type SnapshotAvailability = PlannerResolvedStatus;

type SnapshotMember = {
  id: string;
  name: string;
  avatar: string | null;
  roles: string[];
  availability: SnapshotAvailability;
};

type SnapshotTeam = {
  id: string;
  name: string;
  members: SnapshotMember[];
};

type PlannerTeamAvailabilitySnapshotProps = {
  serviceId?: string;
  title?: string;
  compact?: boolean;
};

const PLANNER_TEAM_LABELS = new Map(PLANNER_TEAM_DEFINITIONS.map((team) => [team.id, team.label]));

function formatTeamArea(area?: string | null): string {
  const raw = (area || '').trim();
  if (!raw) return 'General';
  return raw.replace(/_/g, ' ');
}

function getInitials(name: string): string {
  const tokens = name
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (!tokens.length) return 'NA';
  return tokens
    .slice(0, 2)
    .map((token) => token[0]?.toUpperCase() ?? '')
    .join('');
}

function overlapsServiceDate(serviceDate: string, startsAt: string, endsAt: string): boolean {
  const serviceMs = new Date(serviceDate).getTime();
  const startMs = new Date(startsAt).getTime();
  const endMs = new Date(endsAt).getTime();
  if (Number.isNaN(serviceMs) || Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
  return startMs <= serviceMs && endMs > serviceMs;
}

function availabilityMeta(status: SnapshotAvailability): { tone: SnapshotAvailability; label: string } {
  return {
    tone: status,
    label: formatPlannerStatusLabel(status, 'snapshot'),
  };
}

export default function PlannerTeamAvailabilitySnapshot({
  serviceId,
  title = 'Staff availability',
  compact = false,
}: PlannerTeamAvailabilitySnapshotProps) {
  const { user } = useAuth();
  const planner = useChurchPlanner(true, user?.id);
  const rolesQuery = usePlannerRoles(true);

  const plan = useMemo(
    () => (serviceId ? planner.plans.find((entry) => entry.id === serviceId) || null : planner.nextPlan || null),
    [planner.nextPlan, planner.plans, serviceId]
  );

  const roleById = useMemo(() => {
    const map = new Map<string, { id: string; name: string; area?: string | null }>();
    (rolesQuery.data ?? []).forEach((role) => {
      map.set(role.id, role);
    });
    return map;
  }, [rolesQuery.data]);

  const rolesByName = useMemo(() => {
    const map = new Map<string, Array<{ id: string; name: string; area?: string | null }>>();
    roleById.forEach((role) => {
      const key = role.name.trim().toLowerCase();
      const existing = map.get(key) ?? [];
      existing.push(role);
      map.set(key, existing);
    });
    return map;
  }, [roleById]);

  const availabilityByUserId = useMemo(() => {
    const map = new Map<string, string>();
    if (!plan) return map;
    (planner.availability ?? []).forEach((entry) => {
      if (!entry.user_id || entry.service_plan_id !== plan.id) return;
      map.set(entry.user_id, entry.availability_status || '');
    });
    return map;
  }, [plan, planner.availability]);

  const assignmentStatusByUserId = useMemo(() => {
    const map = new Map<string, SnapshotAvailability>();
    if (!plan) return map;

    (plan.service_plan_assignments ?? []).forEach((assignment) => {
      if (!assignment.user_id) return;
      const tone = normalizePlannerStatus(assignment.status);
      const merged = mergePlannerStatuses(map.get(assignment.user_id), tone);
      map.set(assignment.user_id, merged);
    });

    return map;
  }, [plan]);

  const unavailableUserIds = useMemo(() => {
    const set = new Set<string>();
    if (!plan) return set;
    (planner.memberUnavailability ?? []).forEach((entry) => {
      if (!entry.user_id) return;
      if (!overlapsServiceDate(plan.service_date, entry.starts_at, entry.ends_at)) return;
      set.add(entry.user_id);
    });
    return set;
  }, [plan, planner.memberUnavailability]);

  const teams = useMemo<SnapshotTeam[]>(() => {
    if (!plan) return [];

    const rosterByTeam = new Map<
      string,
      {
        id: string;
        name: string;
        members: Map<string, { name: string; avatar: string | null; roles: Set<string>; availability: SnapshotAvailability }>;
      }
    >();

    planner.people.forEach((person) => {
      const resolvedRoles = new Map<string, { id: string; name: string; area?: string | null }>();
      [person.primaryPlannerRoleId, person.defaultPlannerRoleId, ...person.secondaryPlannerRoleIds].forEach((roleId) => {
        if (!roleId) return;
        const role = roleById.get(roleId);
        if (role) resolvedRoles.set(role.id, role);
      });

      if (!resolvedRoles.size) {
        person.roleNames.forEach((roleName) => {
          const matches = rolesByName.get(roleName.trim().toLowerCase()) ?? [];
          matches.forEach((role) => resolvedRoles.set(role.id, role));
        });
      }

      const memberAvailability = resolvePlannerServiceStatus({
        assignmentStatus: assignmentStatusByUserId.get(person.id),
        availabilityStatus: availabilityByUserId.get(person.id),
        hasCalendarBlock: unavailableUserIds.has(person.id),
      });

      const addMemberToTeam = (teamKey: string, teamName: string, roleNames: string[]) => {
        const teamEntry =
          rosterByTeam.get(teamKey) || {
            id: teamKey,
            name: teamName,
            members: new Map<string, { name: string; avatar: string | null; roles: Set<string>; availability: SnapshotAvailability }>(),
          };

        const existing = teamEntry.members.get(person.id) || {
          name: person.name,
          avatar: person.avatar,
          roles: new Set<string>(),
          availability: memberAvailability,
        };

        roleNames.forEach((roleName) => {
          if (roleName) existing.roles.add(roleName);
        });
        existing.availability = memberAvailability;
        teamEntry.members.set(person.id, existing);
        rosterByTeam.set(teamKey, teamEntry);
      };

      if (!resolvedRoles.size) {
        addMemberToTeam('team:general', 'General', person.roleNames);
        return;
      }

      const teamRoleMap = new Map<string, { teamName: string; roleNames: Set<string> }>();
      Array.from(resolvedRoles.values()).forEach((role) => {
        const canonicalTeamId = resolveTeamIdFromRole(role);
        const fallbackTeamName = formatTeamArea(role.area);
        const teamKey = canonicalTeamId ? `team:${canonicalTeamId}` : `custom:${fallbackTeamName.toLowerCase()}`;
        const entry = teamRoleMap.get(teamKey) ?? {
          teamName: canonicalTeamId ? PLANNER_TEAM_LABELS.get(canonicalTeamId) || fallbackTeamName : fallbackTeamName,
          roleNames: new Set<string>(),
        };
        if (role.name) entry.roleNames.add(role.name);
        teamRoleMap.set(teamKey, entry);
      });

      teamRoleMap.forEach((entry, teamKey) => {
        addMemberToTeam(teamKey, entry.teamName, Array.from(entry.roleNames));
      });
    });

    return Array.from(rosterByTeam.entries())
      .map(([, teamEntry]) => {
        const members = Array.from(teamEntry.members.entries())
          .map(([userId, person]) => ({
            id: userId,
            name: person.name,
            avatar: person.avatar,
            roles: Array.from(person.roles).sort((a, b) => a.localeCompare(b)),
            availability: person.availability,
          }))
          .sort((a, b) => {
            const availabilityOrder: Record<SnapshotAvailability, number> = {
              confirmed: 0,
              available: 1,
              maybe: 2,
              unavailable: 3,
            };
            const toneCompare = availabilityOrder[a.availability] - availabilityOrder[b.availability];
            if (toneCompare !== 0) return toneCompare;
            return a.name.localeCompare(b.name);
          });

        return {
          id: teamEntry.id,
          name: teamEntry.name,
          members,
        };
      })
      .sort((a, b) => {
        if (a.members.length !== b.members.length) {
          return b.members.length - a.members.length;
        }
        return a.name.localeCompare(b.name);
      });
  }, [assignmentStatusByUserId, availabilityByUserId, plan, planner.people, roleById, rolesByName, unavailableUserIds]);

  return (
    <SnapshotCard $compact={compact}>
      <SnapshotHead>
        <SnapshotTitle>{title}</SnapshotTitle>
        <FaUsers size={16} color={plannerColors.muted} />
      </SnapshotHead>
      {plan ? (
        <PlannerSubtle style={{ margin: '-0.15rem 0 0.75rem' }}>
          {plan.title} • {new Date(plan.service_date).toLocaleDateString()}
        </PlannerSubtle>
      ) : null}
      {planner.isLoading || rolesQuery.isLoading ? (
        <ActivityList>
          <ActivityItem>Loading staff availability...</ActivityItem>
        </ActivityList>
      ) : planner.isError || rolesQuery.isError ? (
        <ActivityList>
          <ActivityItem>Unable to load staff availability right now.</ActivityItem>
        </ActivityList>
      ) : !plan ? (
        <ActivityList>
          <ActivityItem>No upcoming service found.</ActivityItem>
        </ActivityList>
      ) : teams.length === 0 ? (
        <ActivityList>
          <ActivityItem>No staff roster is configured for this service yet.</ActivityItem>
        </ActivityList>
      ) : (
        <TeamGroupList>
          {teams.map((team) => (
            <TeamGroupCard key={team.id}>
              <TeamGroupHeader>
                <TeamGroupTitle>{team.name}</TeamGroupTitle>
                <TeamGroupCount>{team.members.length} members</TeamGroupCount>
              </TeamGroupHeader>

              {team.members.length ? (
                <TeamList>
                  {team.members.map((member) => (
                    <TeamMemberItem key={`${team.id}-${member.id}`}>
                      <TeamMemberLink to={`/church-planner/people/${member.id}`}>
                        {member.avatar ? (
                          <TeamMemberAvatar src={member.avatar} alt={member.name} />
                        ) : (
                          <TeamMemberAvatarFallback>{getInitials(member.name)}</TeamMemberAvatarFallback>
                        )}
                        <TeamMemberText>
                          <TeamMemberName>{member.name}</TeamMemberName>
                          <TeamMemberMeta>{member.roles.length ? member.roles.join(' / ') : 'Planner staff'}</TeamMemberMeta>
                        </TeamMemberText>
                      </TeamMemberLink>
                      <MemberStatusPill $tone={member.availability}>
                        {availabilityMeta(member.availability).label}
                      </MemberStatusPill>
                    </TeamMemberItem>
                  ))}
                </TeamList>
              ) : (
                <PlannerSubtle>No team members yet.</PlannerSubtle>
              )}
            </TeamGroupCard>
          ))}
        </TeamGroupList>
      )}
    </SnapshotCard>
  );
}